'use client';

import React, { useState, useEffect } from 'react';
import { Button } from '@heroui/button';
import { Input } from '@heroui/input';
import Select from "react-select";
import { TzSelectStyles, TzSelectStylesFullWidth } from '@/shared/styleDefs';
import { Spinner } from '@heroui/spinner';
import { listOrganizations } from '@/lib/admin-organizations';
import { getTokenForHeimdallAdmin } from '@/lib/heimdall-admin-token';
import { CreateTenantRequest, TenantDTO, OrganizationDTO, TIMEZONE_OPTIONS } from '@/shared/types';
import { TenantBannersAndRemoteStorage } from './TenantBannersAndRemoteStorage';
import { validateRemoteStorage, sanitizeBannersAndRemoteStorage } from './tenant-form-shared';
export interface CreateTenantFormProps {
  idToken: string | null;
  onSubmit: (data: CreateTenantRequest) => Promise<TenantDTO>;
  onSuccess: (tenant: TenantDTO) => void;
  onCancel: () => void;
  /** Pre-select and lock the organization field (e.g. when launched from an org card). */
  defaultOrganizationId?: string;
  defaultOrganizationName?: string;
  defaultOrganizationAuthArchitecture?: string;
}
const initialForm: CreateTenantRequest = {
  organization_id: '',
  tenant_name: '',
  tenant_code: '',
  timezone: 'America/New_York',
  banners: [],
  remote_storage_type: undefined,
  remote_storage_url: '',
  multi_tenant_friendly_name: ''
};
export function CreateTenantForm({
  idToken,
  onSubmit,
  onSuccess,
  onCancel,
  defaultOrganizationId,
  defaultOrganizationName,
  defaultOrganizationAuthArchitecture
}: CreateTenantFormProps) {
  const [formData, setFormData] = useState<CreateTenantRequest>({
    ...initialForm,
    organization_id: defaultOrganizationId ?? ''
  });
  const [organizations, setOrganizations] = useState<OrganizationDTO[]>([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!idToken) {
      setOrganizationsLoading(false);
      return;
    }
    setOrganizationsLoading(true);
    getTokenForHeimdallAdmin(idToken).then(async token => {
      if (!token) return {
        organizations: []
      };
      return listOrganizations(token, 0, 500);
    }).then(res => setOrganizations(res.organizations ?? [])).catch(() => setOrganizations([])).finally(() => setOrganizationsLoading(false));
  }, [idToken]);
  const selectedOrg = organizations.find(o => o.id === formData.organization_id);
  const selectedAuthArchitecture = selectedOrg?.auth_architecture ?? (formData.organization_id === defaultOrganizationId ? defaultOrganizationAuthArchitecture : undefined);
  const isMultiTenant = selectedAuthArchitecture === 'cognito-multi-tenant';
  const handleChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };
  const setBanner = (index: number, value: string) => {
    setFormData(prev => {
      const next = [...prev.banners];
      next[index] = value;
      return {
        ...prev,
        banners: next
      };
    });
    setError(null);
  };
  const addBanner = () => {
    setFormData(prev => ({
      ...prev,
      banners: [...prev.banners, '']
    }));
    setError(null);
  };
  const removeBanner = (index: number) => {
    setFormData(prev => ({
      ...prev,
      banners: prev.banners.filter((_, i) => i !== index)
    }));
    setError(null);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateRemoteStorage(formData.remote_storage_type, formData.remote_storage_url);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      setIsSubmitting(true);
      setError(null);
      const payload = sanitizeBannersAndRemoteStorage({
        ...formData
      });
      const tenant = await onSubmit(payload);
      onSuccess(tenant);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tenant');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <form onSubmit={handleSubmit} className="flex flex-col gap-6 [&_input]:!text-white [&_[data-slot='value']]:!text-white" data-sentry-component="CreateTenantForm" data-sentry-source-file="CreateTenantForm.tsx">
      {error && <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-4 text-red-300 text-sm">
          {error}
        </div>}

      <div className="relative z-20 bg-slate-800/50 backdrop-blur-xl rounded-lg border border-purple-700/50 p-6 space-y-4">
        <h2 className="text-xl font-semibold text-purple-300 mb-4">Organization</h2>
        {defaultOrganizationId ? <div className="flex flex-col gap-1">
            <span className="text-sm text-slate-300">Organization</span>
            <div className="px-3 py-2 rounded-lg bg-slate-700/30 border border-slate-600 text-white text-sm">
              {defaultOrganizationName ?? defaultOrganizationId}
            </div>
          </div> : organizationsLoading ? <div className="flex items-center gap-2 text-slate-400">
            <Spinner size="sm" />
            <span>Loading organizations...</span>
          </div> : <Select classNamePrefix="react-select" aria-label="Organization" placeholder="Select an organization..." styles={{
        ...TzSelectStylesFullWidth,
        menu: (provided, state) => ({
          ...(TzSelectStylesFullWidth.menu?.(provided, state) ?? provided),
          zIndex: 9999,
          color: 'black'
        })
      }} required className="react-select-container font-inter pr-2 w-full custom-scrollbar2" value={formData.organization_id ? {
        value: formData.organization_id,
        label: organizations.find(o => o.id === formData.organization_id)?.name ?? formData.organization_id
      } : null} onChange={(newValue: unknown) => {
        const option = newValue as {
          value: string;
          label: string;
        } | null;
        const orgId = option?.value ?? '';
        const org = organizations.find(o => o.id === orgId);
        setFormData(prev => ({
          ...prev,
          organization_id: orgId,
          ...(org?.auth_architecture !== 'cognito-multi-tenant' ? {
            multi_tenant_friendly_name: ''
          } : {})
        }));
        setError(null);
      }} options={organizations.filter(org => org.status === 'active' && !!org.can_add_tenants).map(org => ({
        value: org.id,
        label: org.name
      }))} noOptionsMessage={() => "No options available"} />}
      </div>

      <div className="relative z-10 bg-slate-800/50 backdrop-blur-xl rounded-lg border border-purple-700/50 p-6 space-y-6">
        <h2 className="text-xl font-semibold text-purple-300 mb-4">Tenant Information</h2>
        <Input label="Tenant Name" aria-label="Tenant Name" name="tenant_name" value={formData.tenant_name} onValueChange={v => handleChange('tenant_name', v)} required labelPlacement="outside" placeholder="e.g., City of Springfield" classNames={{
        input: "box-border text-white",
        inputWrapper: "box-border"
      }} data-sentry-element="Input" data-sentry-source-file="CreateTenantForm.tsx" />
        <Input label="Tenant Friendly Name" aria-label="Tenant Friendly Name" name="tenant_code" value={formData.tenant_code} onValueChange={v => handleChange('tenant_code', v)} required labelPlacement="outside" placeholder="e.g., us-mo-fcda" classNames={{
        input: "box-border text-white",
        inputWrapper: "box-border"
      }} data-sentry-element="Input" data-sentry-source-file="CreateTenantForm.tsx" />
        {isMultiTenant && <Input label="Karpel Friendly Name" aria-label="Karpel Friendly Name" name="multi_tenant_friendly_name" value={formData.multi_tenant_friendly_name} onValueChange={v => handleChange('multi_tenant_friendly_name', v)} labelPlacement="outside" placeholder="e.g., idcassia" classNames={{
        input: "box-border text-white",
        inputWrapper: "box-border"
      }} />}
        <label className="flex text-white/100 font-light tracking-wider text-[14px] m-0 mb-2 p-0">Time Zone</label>
        <Select classNamePrefix="react-select" aria-label="Time Zone (optional)" styles={{
        ...TzSelectStyles,
        menu: (provided, state) => ({
          ...(TzSelectStyles.menu?.(provided, state) ?? provided),
          zIndex: 9999,
          color: 'black'
        })
      }} value={formData.timezone ? {
        value: formData.timezone,
        label: TIMEZONE_OPTIONS.find(o => o.value === formData.timezone)?.label ?? formData.timezone
      } : null} onChange={(newValue: unknown) => {
        const option = newValue as {
          value: string;
          label: string;
        } | null;
        handleChange('timezone', option?.value ?? '');
      }} className="react-select-container font-inter pr-2 w-full custom-scrollbar2" options={TIMEZONE_OPTIONS.map(option => ({
        value: option.value,
        label: option.label
      }))} noOptionsMessage={() => "No options available"} data-sentry-element="Select" data-sentry-source-file="CreateTenantForm.tsx" />
      </div>

      <TenantBannersAndRemoteStorage banners={formData.banners} onBannerChange={setBanner} onAddBanner={addBanner} onRemoveBanner={removeBanner} remoteStorageType={formData.remote_storage_type} remoteStorageUrl={formData.remote_storage_url ?? ''} onRemoteStorageTypeChange={type => setFormData(prev => ({
      ...prev,
      remote_storage_type: type,
      ...(type == null ? {
        remote_storage_url: ''
      } : {})
    }))} onRemoteStorageUrlChange={url => handleChange('remote_storage_url', url)} onClearError={() => setError(null)} data-sentry-element="TenantBannersAndRemoteStorage" data-sentry-source-file="CreateTenantForm.tsx" />

      <div className="flex justify-end gap-4 pt-2">
        <Button type="button" variant="light" onPress={onCancel} className="text-slate-300 hover:text-white" data-sentry-element="Button" data-sentry-source-file="CreateTenantForm.tsx">
          Cancel
        </Button>
        <Button type="submit" isLoading={isSubmitting} isDisabled={!formData.organization_id?.trim() || !formData.tenant_name?.trim() || !formData.tenant_code?.trim()} className="bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg" data-sentry-element="Button" data-sentry-source-file="CreateTenantForm.tsx">
          Create Tenant
        </Button>
      </div>
    </form>;
}